// src/Pages/ReceptionistDashboard/CheckInSystem.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  ListItemAvatar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  AssignmentInd as AssignmentIndIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  AccessTime as TimeIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GroupAdd as RegistrationIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../../api/axiosInstance';

const CheckInSystem = () => {
  // Main state
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Registration dialogs
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [registrationMode, setRegistrationMode] = useState('quick'); // 'quick' or 'full'
  const [registrationStep, setRegistrationStep] = useState(0);

  // Quick registration form
  const [quickRegisterForm, setQuickRegisterForm] = useState({
    name: '',
    phone: '',
    email: '',
    sendMagicLink: true,
    channel: 'sms'
  });

  // Full registration form
  const [fullRegisterForm, setFullRegisterForm] = useState({
    name: '',
    phone: '',
    email: '',
    age: '',
    gender: '',
    city: '',
    address: '',
    emergencyContact: '',
    medicalHistory: '',
    sendMagicLink: true,
    channel: 'sms'
  });

  // Appointment booking
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    doctorId: '',
    date: '',
    time: '',
    reason: '',
    urgency: 'normal'
  });

  // Invoice and payment
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Status colors and icons
  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'Arrived': return 'warning';
      case 'In Room': return 'info';
      case 'Completed': return 'success';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Scheduled': return <ScheduleIcon />;
      case 'Arrived': return <CheckCircleIcon />;
      case 'In Room': return <AssignmentIndIcon />;
      case 'Completed': return <CheckIcon />;
      default: return <InfoIcon />;
    }
  };

  // Fetch data
  const fetchTodayAppointments = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/appointments/receptionist/appointments/today/');
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setNotification({
        message: 'Failed to fetch appointments',
        severity: 'error'
      });
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/appointments/receptionist/patients/');
      setPatients(response.data.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setNotification({
        message: 'Failed to fetch patients',
        severity: 'error'
      });
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/doctors/clinic/1/');
      setDoctors(response.data.doctors || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setNotification({
        message: 'Failed to fetch doctors',
        severity: 'error'
      });
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTodayAppointments(),
        fetchPatients(),
        fetchDoctors()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchTodayAppointments, fetchPatients, fetchDoctors]);

  // Registration handlers
  const handleQuickRegister = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/v1/auth/quick-create-patient/', quickRegisterForm);
      
      if (response.data.magic_link) {
        setNotification({
          message: `Patient created successfully! Magic link sent via ${quickRegisterForm.channel}.`,
          severity: 'success'
        });
      } else {
        setNotification({
          message: 'Patient created successfully!',
          severity: 'success'
        });
      }
      
      // Reset form and close dialog
      setQuickRegisterForm({
        name: '',
        phone: '',
        email: '',
        sendMagicLink: true,
        channel: 'sms'
      });
      setRegistrationDialogOpen(false);
      setRegistrationStep(0);
      
      // Refresh data
      await fetchPatients();
      await fetchTodayAppointments();
      
    } catch (error) {
      console.error('Error creating patient:', error);
      setNotification({
        message: error.response?.data?.message || 'Failed to create patient',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFullRegister = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/v1/auth/quick-create-patient/', fullRegisterForm);
      
      setNotification({
        message: 'Patient registered successfully with full details!',
        severity: 'success'
      });
      
      // Reset form and close dialog
      setFullRegisterForm({
        name: '',
        phone: '',
        email: '',
        age: '',
        gender: '',
        city: '',
        address: '',
        emergencyContact: '',
        medicalHistory: '',
        sendMagicLink: true,
        channel: 'sms'
      });
      setRegistrationDialogOpen(false);
      setRegistrationStep(0);
      
      // Refresh data
      await fetchPatients();
      await fetchTodayAppointments();
      
    } catch (error) {
      console.error('Error creating patient:', error);
      setNotification({
        message: error.response?.data?.message || 'Failed to create patient',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Appointment booking
  const handleBookAppointment = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/appointments/receptionist/appointments/', {
        patient_id: selectedPatient.id,
        doctor_id: appointmentForm.doctorId,
        scheduled_date: appointmentForm.date,
        scheduled_time: appointmentForm.time,
        reason: appointmentForm.reason,
        urgency: appointmentForm.urgency
      });
      
      setNotification({
        message: 'Appointment booked successfully!',
        severity: 'success'
      });
      
      // Reset form and close dialog
      setAppointmentForm({
        doctorId: '',
        date: '',
        time: '',
        reason: '',
        urgency: 'normal'
      });
      setAppointmentDialogOpen(false);
      setSelectedPatient(null);
      
      // Refresh appointments
      await fetchTodayAppointments();
      
    } catch (error) {
      console.error('Error booking appointment:', error);
      setNotification({
        message: error.response?.data?.message || 'Failed to book appointment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Update appointment status
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await axiosInstance.patch(`/appointments/receptionist/appointments/${appointmentId}/status/`, {
        status: newStatus
      });
      
      setNotification({
        message: `Appointment status updated to ${newStatus}`,
        severity: 'success'
      });
      
      // Refresh appointments
      await fetchTodayAppointments();
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        message: error.response?.data?.error || 'Failed to update status',
        severity: 'error'
      });
    }
  };

  // Generate invoice
  const handleGenerateInvoice = async (appointmentId) => {
    try {
      const response = await axiosInstance.post(`/appointments/receptionist/invoices/${appointmentId}/generate/`);
      
      setNotification({
        message: 'Invoice generated successfully!',
        severity: 'success'
      });
      
      setInvoiceDialogOpen(false);
      await fetchTodayAppointments();
    } catch (error) {
      console.error('Error generating invoice:', error);
      setNotification({
        message: error.response?.data?.error || 'Failed to generate invoice',
        severity: 'error'
      });
    }
  };

  // Mark payment as received
  const handleMarkPayment = async (invoiceId) => {
    try {
      await axiosInstance.patch(`/appointments/receptionist/invoices/${invoiceId}/pay/`);
      
      setNotification({
        message: 'Payment confirmed successfully!',
        severity: 'success'
      });
      
      setInvoiceDialogOpen(false);
      await fetchTodayAppointments();
    } catch (error) {
      console.error('Error processing payment:', error);
      setNotification({
        message: error.response?.data?.error || 'Failed to process payment',
        severity: 'error'
      });
    }
  };

  // Filter appointments
  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = appointment.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         appointment.doctor_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || appointment.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Registration steps
  const registrationSteps = [
    {
      label: 'Patient Details',
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={registrationMode === 'quick' ? quickRegisterForm.name : fullRegisterForm.name}
                onChange={(e) => {
                  if (registrationMode === 'quick') {
                    setQuickRegisterForm(prev => ({ ...prev, name: e.target.value }));
                  } else {
                    setFullRegisterForm(prev => ({ ...prev, name: e.target.value }));
                  }
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={registrationMode === 'quick' ? quickRegisterForm.phone : fullRegisterForm.phone}
                onChange={(e) => {
                  if (registrationMode === 'quick') {
                    setQuickRegisterForm(prev => ({ ...prev, phone: e.target.value }));
                  } else {
                    setFullRegisterForm(prev => ({ ...prev, phone: e.target.value }));
                  }
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Optional)"
                value={registrationMode === 'quick' ? quickRegisterForm.email : fullRegisterForm.email}
                onChange={(e) => {
                  if (registrationMode === 'quick') {
                    setQuickRegisterForm(prev => ({ ...prev, email: e.target.value }));
                  } else {
                    setFullRegisterForm(prev => ({ ...prev, email: e.target.value }));
                  }
                }}
              />
            </Grid>
            {registrationMode === 'full' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    value={fullRegisterForm.age}
                    onChange={(e) => setFullRegisterForm(prev => ({ ...prev, age: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={fullRegisterForm.gender}
                      onChange={(e) => setFullRegisterForm(prev => ({ ...prev, gender: e.target.value }))}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={fullRegisterForm.city}
                    onChange={(e) => setFullRegisterForm(prev => ({ ...prev, city: e.target.value }))}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )
    },
    {
      label: 'Notification Settings',
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={registrationMode === 'quick' ? quickRegisterForm.sendMagicLink : fullRegisterForm.sendMagicLink}
                    onChange={(e) => {
                      if (registrationMode === 'quick') {
                        setQuickRegisterForm(prev => ({ ...prev, sendMagicLink: e.target.checked }));
                      } else {
                        setFullRegisterForm(prev => ({ ...prev, sendMagicLink: e.target.checked }));
                      }
                    }}
                  />
                }
                label="Send magic link for profile completion"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Notification Channel</InputLabel>
                <Select
                  value={registrationMode === 'quick' ? quickRegisterForm.channel : fullRegisterForm.channel}
                  onChange={(e) => {
                    if (registrationMode === 'quick') {
                      setQuickRegisterForm(prev => ({ ...prev, channel: e.target.value }));
                    } else {
                      setFullRegisterForm(prev => ({ ...prev, channel: e.target.value }));
                    }
                  }}
                >
                  <MenuItem value="sms">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon /> SMS
                    </Box>
                  </MenuItem>
                  <MenuItem value="email">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon /> Email
                    </Box>
                  </MenuItem>
                  <MenuItem value="whatsapp">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsAppIcon /> WhatsApp
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Patient Check-In System
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage patient registrations, appointments, and check-ins
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setRegistrationMode('quick');
                setRegistrationStep(0);
                setRegistrationDialogOpen(true);
              }}
            >
              Quick Register Patient
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setRegistrationMode('full');
                setRegistrationStep(0);
                setRegistrationDialogOpen(true);
              }}
            >
              Full Register Patient
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setAppointmentDialogOpen(true)}
            >
              Book Appointment
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<RegistrationIcon />}
              onClick={() => window.location.href = '/receptionist-dashboard/registration'}
            >
              Advanced Registration
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Today's Appointments (${filteredAppointments.length})`} />
          <Tab label={`All Patients (${patients.length})`} />
          <Tab label="Doctors & Availability" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Search and Filter */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  placeholder="Search appointments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="Scheduled">Scheduled</MenuItem>
                    <MenuItem value="Arrived">Arrived</MenuItem>
                    <MenuItem value="In Room">In Room</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchTodayAppointments}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Appointments List */}
          <Grid container spacing={2}>
            {filteredAppointments.map((appointment) => (
              <Grid item xs={12} key={appointment.id}>
                <Card>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {appointment.patient_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {appointment.patient_phone}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DoctorIcon color="primary" />
                          <Typography variant="body2">
                            Dr. {appointment.doctor_name}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon color="primary" />
                          <Typography variant="body2">
                            {format(new Date(appointment.scheduled_time), 'HH:mm')}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Chip
                          icon={getStatusIcon(appointment.status)}
                          label={appointment.status}
                          color={getStatusColor(appointment.status)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Stack direction="row" spacing={1}>
                          {appointment.status === 'Scheduled' && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => updateAppointmentStatus(appointment.id, 'Arrived')}
                            >
                              Check In
                            </Button>
                          )}
                          {appointment.status === 'Arrived' && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => updateAppointmentStatus(appointment.id, 'In Room')}
                            >
                              Send to Room
                            </Button>
                          )}
                          {appointment.status === 'In Room' && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => updateAppointmentStatus(appointment.id, 'Completed')}
                            >
                              Complete
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReceiptIcon />}
                            onClick={() => {
                              setSelectedAppointment(appointment);
                              setInvoiceDialogOpen(true);
                            }}
                          >
                            Invoice
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Patients List */}
          <Grid container spacing={2}>
            {patients.map((patient) => (
              <Grid item xs={12} sm={6} md={4} key={patient.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {patient.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {patient.phone}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ScheduleIcon />}
                        onClick={() => {
                          setSelectedPatient(patient);
                          setAppointmentDialogOpen(true);
                        }}
                      >
                        Book Appointment
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                      >
                        View Details
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Doctors List */}
          <Grid container spacing={2}>
            {doctors.map((doctor) => (
              <Grid item xs={12} sm={6} md={4} key={doctor.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                        <DoctorIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Dr. {doctor.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {doctor.specialization}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <LocationIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {doctor.clinic_name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <TimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      ₹{doctor.consultation_fee}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ScheduleIcon />}
                    >
                      View Schedule
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Registration Dialog */}
      <Dialog
        open={registrationDialogOpen}
        onClose={() => setRegistrationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {registrationMode === 'quick' ? 'Quick Register Patient' : 'Full Register Patient'}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={registrationStep} orientation="vertical">
            {registrationSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {step.content}
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <div>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (registrationStep === registrationSteps.length - 1) {
                            if (registrationMode === 'quick') {
                              handleQuickRegister();
                            } else {
                              handleFullRegister();
                            }
                          } else {
                            setRegistrationStep((prevStep) => prevStep + 1);
                          }
                        }}
                        sx={{ mr: 1 }}
                      >
                        {registrationStep === registrationSteps.length - 1 ? 'Register Patient' : 'Continue'}
                      </Button>
                      <Button
                        disabled={registrationStep === 0}
                        onClick={() => setRegistrationStep((prevStep) => prevStep - 1)}
                      >
                        Back
                      </Button>
                    </div>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrationDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Booking Dialog */}
      <Dialog
        open={appointmentDialogOpen}
        onClose={() => setAppointmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Book Appointment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Patient</InputLabel>
                <Select
                  value={selectedPatient?.id || ''}
                  onChange={(e) => {
                    const patient = patients.find(p => p.id === e.target.value);
                    setSelectedPatient(patient);
                  }}
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.id} value={patient.id}>
                      {patient.name} - {patient.phone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Select Doctor</InputLabel>
                <Select
                  value={appointmentForm.doctorId}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, doctorId: e.target.value }))}
                >
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.name} - {doctor.specialization}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={appointmentForm.date}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={appointmentForm.time}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={appointmentForm.urgency}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, urgency: e.target.value }))}
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="emergency">Emergency</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Visit"
                multiline
                rows={3}
                value={appointmentForm.reason}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppointmentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBookAppointment}
            disabled={!selectedPatient || !appointmentForm.doctorId || !appointmentForm.date || !appointmentForm.time}
          >
            Book Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invoice & Payment</DialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Appointment Details
              </Typography>
              <Typography variant="body2" gutterBottom>
                Patient: {selectedAppointment.patient_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Doctor: Dr. {selectedAppointment.doctor_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Date: {format(new Date(selectedAppointment.scheduled_date), 'PPP')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Time: {format(new Date(selectedAppointment.scheduled_time), 'HH:mm')}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Payment Status
              </Typography>
              <Typography variant="body2" gutterBottom>
                Consultation Fee: ₹{selectedAppointment.consultation_fee || 500}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => handleGenerateInvoice(selectedAppointment?.id)}
          >
            Generate Invoice
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleMarkPayment(selectedAppointment?.invoice_id)}
          >
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckInSystem;
